import styled from "styled-components";
import {getTheme} from "../../../../shared/themeUtils";
import {request} from "../../../../shared/httpInterceptor";
import {ISeriesSelect} from "../../../../interfaces/ISeriesSelect";
import {useCountryLang} from "../../../../hooks/useCountryLang";
import {useRouter} from "next/router";
import React, {useContext, useEffect, useState} from "react";
import {ISelectType} from "../../../../interfaces/ISelectType";
import {UrlAttrFilterQueryContext} from "../../../../contexts/UrlAttrFilterQueryContext";
import {useMessages} from "../../../../hooks/useMessages";
import {useClientContext} from "../../../../hooks/useContext";
import {useQuery} from "@tanstack/react-query";
import LoadMoreProductListing from "../../../../components/product/LoadMoreProductListing";
import PageHead from "../../../../components/PageHead";
import BrandHeadingPlaceholder from "../../../../components/placeholders/brand/BrandHeadingPlaceholder";
import Select from "react-select";
import {ISubSerie} from "../../../../interfaces/ISubSerie";
import {ISubSeriesSelect} from "../../../../interfaces/ISubseriesSelect";
import BreadcrumbsPlaceholder from "../../../../components/placeholders/BreadcrumbsPlaceholder";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Link from "next/link";
import {getNavPath} from "../../../../shared/navigateUtil";
import Image from "next/legacy/image";
import {withServerSideProps} from "../../../../shared/withServerSideProps";
import {IWebBanner} from "../../../../interfaces/IWebBanner";
import WebBanner from "../../../../components/WebBanner";
import {extractIdFromUrl} from "../../../../shared/utils";
import NotFound from "../../../../components/NotFound";
import {NotFoundType} from "../../../../shared/enums";

const PageContain = styled.div`
    margin: ${getTheme("space.2")} 0;

    .load-more {
        width: 100%;
        display: flex;
        justify-content: center;
        padding: ${getTheme("space.2")} 0;
    }

    .description {
        white-space: pre-wrap;
    }

    .series-subseries {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;

        @media (max-width: ${getTheme("breakpoints.2")}) {
            flex-direction: column;
        }
    }
`

const SeriesTitle = styled.div`
    display: flex;
    align-items: center;
    padding: ${getTheme("space.3")} 0;

    .brand-img {
        margin-right: ${getTheme("space.3")};
        display: flex;
        cursor: pointer;

        img {
            max-height: 70px
        }
    }

    h1 {
        border-left: 1px solid #ddd;
        padding-left: ${getTheme("space.3")};
        margin-bottom: 0;
    }
`

const SelectSeries = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: ${getTheme("space.3")};
    width: auto;
    flex-grow: 1;
    max-width: 450px;

    .select-label {
        margin-right: ${getTheme("space.1")};
        font-weight: bold;
    }

    .series-select {
        max-width: 300px;
        width: 100%;
    }
`

const fetchSubSeriesInfo = async (subSeriesId: number | null, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/subSeries/${subSeriesId}`,
    method: 'GET',
    host: host,
    cookies: cookies
  })
  return res.data;
}

const fetchBreadcrumbs = async (subSeriesId: number | null, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/subSeries/breadcrumbs/${subSeriesId}`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}

const fetchAllSeries = async (brandId: number | null, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/series/byBrand/${brandId}`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}


const fetchAllSubSeries = async (seriesId: number, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/subSeries/bySeries/${seriesId}`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}
const fetchSubSeriesBanner = async (subSeriesId: number | null, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/banner/subSeries/${subSeriesId}?orientationCode=horizontal`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}


const SubSeries = () => {
  const countryLang = useCountryLang()
  const router = useRouter()
  const [pageSize, setPageSize] = useState(30)
  const [subSeriesId, setSubSeriesId] = useState<number | null>(null)
  const [brandId, setBrandId] = useState<number | null>(null)
  const [seriesId, setSeriesId] = useState(0)
  const [allSeriesOptions, setAllSeriesOptions] = useState<ISelectType[]>([])
  const [allSubSeriesOptions, setAllSubSeriesOptions] = useState<ISelectType[]>([])
  const [selectedSeries, setSelectedSeries] = useState<any>()
  const [selectedSubSeries, setSelectedSubSeries] = useState<any>()
  const {urlFilterQueryCtx} = useContext(UrlAttrFilterQueryContext)
  const [serverStateId, setServerStateId] = useState("")
  const [expectedListingCountRequestUrl, setExpectedListingCountRequestUrl] = useState(`listing/series/${subSeriesId}`)
  const messagesQuery = useMessages()
  const contextQuery = useClientContext()

  const fetchSubSeriesListing = async ({pageParam = 0}) => {
    const paramsArr = [];
    paramsArr.push(`offset=${pageParam}`);
    paramsArr.push(`size=${pageSize}`)
    if (router.query.sort) {
      paramsArr.push(`sort=${router.query.sort.toString()}`)
    }
    if (urlFilterQueryCtx) {
      paramsArr.push(`filter=${urlFilterQueryCtx}`)
    }
    if (serverStateId) {
      paramsArr.push(`state=${serverStateId}`)
    }
    if (router.query.search) {
      paramsArr.push(`search=${router.query.search.toString()}`)
    }
    const res = await request({
      url: `/${countryLang}/listing/subseries/${subSeriesId}?${paramsArr.join('&')}`,
      method: 'get'
    });
    // state param is used server-side for caching
    // we pass it back with every subsequent request
    if (res.data && res.data.state) {
      setServerStateId(res.data.state)
    }
    return res.data;
  }


  const breadcrumbsQuery = useQuery(["subSeries-breadcrumbs", subSeriesId, countryLang],
    () => fetchBreadcrumbs(subSeriesId, countryLang), {
      enabled: subSeriesId != null && countryLang != null,
    })

  const allSeriesQuery = useQuery<ISeriesSelect[]>(["all-series", brandId, countryLang],
    () => fetchAllSeries(brandId, countryLang), {
      enabled: brandId != null && countryLang != null && !isNaN(brandId),
    })

  const allSubSeriesQuery = useQuery<ISubSeriesSelect[]>(["all-subseries", seriesId, countryLang],
    () => fetchAllSubSeries(seriesId, countryLang), {
      enabled: seriesId > 0 && countryLang != null && !isNaN(seriesId),
    })

  const subSeriesQueryInfo = useQuery<ISubSerie>(["subSeries-info", subSeriesId], () =>
    fetchSubSeriesInfo(subSeriesId, countryLang), {
    enabled: countryLang != null && subSeriesId != null && !isNaN(subSeriesId),
  })

  const subSeriesQueryBanner = useQuery<IWebBanner>(
    ['subSeries-banner', subSeriesId, countryLang],
    () => fetchSubSeriesBanner(subSeriesId, countryLang),
    {
      enabled: subSeriesId != null && countryLang != null && !isNaN(subSeriesId),
    }
  );


  useEffect(() => {
    if (!router.isReady) return;

    const id = extractIdFromUrl(router);
    setSubSeriesId(id);
    
    // Only update the expected listing URL if we have a valid ID
    if (id) {
      setExpectedListingCountRequestUrl(`listing/subSeries/${id}`);
    }
  }, [router.isReady, router.asPath]);


  useEffect(() => {
    if (router.query.slug) {
      const splitRoute = router.query.slug.toString().split("-");
      const currentSubSeriesId = parseInt(splitRoute[splitRoute.length - 1])
      setSubSeriesId(currentSubSeriesId)
      setExpectedListingCountRequestUrl(`listing/subseries/${currentSubSeriesId}`)
    }

    if (subSeriesQueryInfo.data) {
      setBrandId(subSeriesQueryInfo.data.brandID);
    }
    if (subSeriesQueryInfo.data && !seriesId) {
      setSeriesId(subSeriesQueryInfo.data.seriesID)
    }

    // transform allSeries response for select
    if (subSeriesQueryInfo.data && allSeriesQuery.data) {
      const selectOptions: ISelectType[] = []
      allSeriesQuery.data.map(item => {
        selectOptions.push({
          label: item.seriesName,
          value: item.seriesSlug
        })
      })
      // set options
      setAllSeriesOptions(selectOptions)
      //@ts-ignore
      const selected = selectOptions.filter(item => item.value.includes(seriesId))
      setSelectedSeries(selected[0])
    }

    // transform allSubSeries response for select
    if (subSeriesQueryInfo.data && allSubSeriesQuery.data) {
      const selectOptions: ISelectType[] = []
      allSubSeriesQuery.data.map(item => {
        selectOptions.push({
          label: item.subSeriesName,
          value: item.subSeriesSlug
        })
      })
      // set options
      setAllSubSeriesOptions(selectOptions)
      const selected = selectOptions.filter(item => item.label == subSeriesQueryInfo.data.subSeriesName)
      if (selected.length > 0) {
        setSelectedSubSeries(selected[0])
      } else {
        setSelectedSubSeries(null)
      }
    }
  }, [router, allSeriesQuery.data, allSubSeriesQuery.data, subSeriesQueryInfo.data])


  const handleSelectSeries = async (option: ISelectType) => {
    // push to url / navigate to selection
    await router.push(`/${countryLang}/series/${option.value}`, undefined, {shallow: true})
  }

  const handleSelectSubSeries = async (option: ISelectType) => {
    // push to url / navigate to selection
    await router.push({
      query: {
        ...(router.query),
        ...(option.label && {slug: option.value})
      }
    }, undefined, {shallow: true})
  }

  // Not Found Layout
  if (subSeriesQueryInfo.data && subSeriesQueryInfo.data.errorID) {
    return (
      <NotFound type={NotFoundType.SERIES}/>
    )
  }

  return (
    <div className="appContainer">
      <PageHead 
        currentPageTitle={subSeriesQueryInfo.data?.subSeriesName || ''}
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
        openGraph={{
          title: subSeriesQueryInfo.data?.subSeriesName || '',
        }}
      />

      <PageContain>
        {!breadcrumbsQuery.isSuccess && <BreadcrumbsPlaceholder/>}
        {breadcrumbsQuery.data && !breadcrumbsQuery.data.exceptionMessage &&
          <Breadcrumbs items={breadcrumbsQuery.data}/>}

        {subSeriesQueryBanner.data && (
          <WebBanner banner={subSeriesQueryBanner.data}/>
        )}

        {!subSeriesQueryInfo.isSuccess && <SeriesTitle><BrandHeadingPlaceholder/></SeriesTitle>}

        {subSeriesQueryInfo.data &&
          <>
            <SeriesTitle>
              <div className="brand-img">
                <Link href={getNavPath(router, `brand/${subSeriesQueryInfo.data.brandSlug}`)}>
                  <Image src={subSeriesQueryInfo.data.brandImage.src}
                         alt={subSeriesQueryInfo.data.brandImage.title}
                         width={"150"}
                         height={"100"}
                         objectFit="contain"/>
                </Link>
              </div>
              <h1>{subSeriesQueryInfo.data.subSeriesName}</h1>
            </SeriesTitle>

            {messagesQuery.data &&
              <div className="series-subseries">
                <SelectSeries>
                  <span className="select-label">{messagesQuery.data.messages["series"]}</span>
                  <Select className="series-select"
                          options={allSeriesOptions}
                          value={selectedSeries}
                          onChange={handleSelectSeries}
                  />
                </SelectSeries>

                <SelectSeries>
                  <span className="select-label">{messagesQuery.data.messages["subseries"]}</span>
                  <Select className="series-select"
                          options={allSubSeriesOptions}
                          value={selectedSubSeries}
                          onChange={handleSelectSubSeries}
                  />
                </SelectSeries>
              </div>
            }
          </>
        }

        {(subSeriesId != null && countryLang != null) &&
          <LoadMoreProductListing
            queryKeyArray={["sub-series-products", subSeriesId]}
            fetchFunction={fetchSubSeriesListing}
            expectedListingCountRequestUrl={expectedListingCountRequestUrl}/>
        }
      </PageContain>
    </div>
  )
}

export const getServerSideProps = withServerSideProps(async (context: any) => {
  const countryLang = `${context.query.country}/${context.query.lang}`

  return {
    props: {}
  }
})


export default SubSeries;
